import { useState, useMemo, useEffect, useRef, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Video, Package, Users, Sparkles, Loader2, Check, ChevronDown, Wand2,
  AlertTriangle, RefreshCw, Download, UserCircle, X, Search, Volume2, Plus, Trash2, ArrowRight, Pencil,
} from "lucide-react";
import type { Persona, Product, Offer, UgcExpressRun, UgcExpressVideo, UgcExpressPronunciation, UgcExpressStyleRecord } from "@shared/schema";
import { UGC_EXPRESS_DEFAULT_STYLE_VALUE } from "@shared/schema";
import { Palette } from "lucide-react";
import { VariantPicker } from "@/components/variant-picker";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { usePickerSearch, PickerSearchHeader, PickerNoMatches } from "@/lib/picker-search";
import { thumbUrl, thumbFallback } from "@/lib/img-thumb";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PastRunsSection, formatRunWhen } from "@/components/past-runs-section";
import { CalendarClock, ChevronUp } from "lucide-react";

type RunWithVideos = { run: UgcExpressRun; videos: UgcExpressVideo[] };

const ACTIVE_STATUSES = new Set(["pending", "rendering"]);
// Statuses that mean a run still needs to stay pinned in the current section:
// actively generating OR parked waiting on the user's script review.
const IN_PROGRESS_STATUSES = new Set(["pending", "awaiting_approval", "rendering"]);

function statusMeta(status: string): { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Video } {
  switch (status) {
    case "pending":
      return { label: "Writing script", variant: "secondary", icon: Sparkles };
    case "awaiting_approval":
      return { label: "Review script", variant: "outline", icon: Pencil };
    case "rendering":
      return { label: "Rendering video", variant: "secondary", icon: Loader2 };
    case "ready":
      return { label: "Ready", variant: "default", icon: Check };
    case "failed":
      return { label: "Failed", variant: "destructive", icon: AlertTriangle };
    default:
      return { label: status, variant: "outline", icon: Video };
  }
}

type PickerOption = { value: string; label: string; imageUrl?: string | null; description?: string | null };
type PickerGroup = { label: string; options: PickerOption[] };

function PickerSelect({
  icon, label, placeholder, value, onChange, options, groups, testId, disabled, searchable, wrapDescription,
}: {
  icon: ReactNode;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  options?: PickerOption[];
  groups?: PickerGroup[];
  testId: string;
  disabled?: boolean;
  searchable?: boolean;
  wrapDescription?: boolean;
}) {
  const filled = !!value && value !== "__none__";
  const allOptions = useMemo<PickerOption[]>(
    () => (groups ? groups.flatMap((g) => g.options) : (options ?? [])),
    [groups, options],
  );
  const hasAvatars = allOptions.some((o) => !!o.imageUrl);
  const selected = filled ? allOptions.find((o) => o.value === value) : undefined;
  const selectedDescription = selected?.description?.trim() || "";
  const [open, setOpen] = useState(false);
  const { showSearch, query, setQuery, searchInputRef, isSearching, matchesLabel } =
    usePickerSearch(open, allOptions.length, !!searchable);
  const matchesQuery = (o: PickerOption) => matchesLabel(o.label);
  const filteredOptions = isSearching ? (options ?? []).filter(matchesQuery) : (options ?? []);
  const filteredGroups = (groups ?? [])
    .map((g) => ({ label: g.label, options: isSearching ? g.options.filter(matchesQuery) : g.options }))
    .filter((g) => g.options.length > 0);
  const hasAnyResult = groups ? filteredGroups.length > 0 : filteredOptions.length > 0;
  const renderOption = (o: PickerOption) => {
    const desc = o.description?.trim() || "";
    return (
      <SelectItem key={o.value} value={o.value} className="text-xs">
        <div className={["flex gap-2 min-w-0", wrapDescription ? "items-start" : "items-center"].join(" ")}>
          {hasAvatars ? (
            o.imageUrl ? (
              <img
                src={thumbUrl(o.imageUrl, 64)!}
                onError={thumbFallback(o.imageUrl)}
                alt=""
                className="h-6 w-6 rounded-full object-cover shrink-0 border border-border/40"
                loading="lazy"
              />
            ) : (
              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center shrink-0 border border-border/40 text-[10px] font-semibold text-muted-foreground">
                {o.label.trim().charAt(0).toUpperCase() || "?"}
              </div>
            )
          ) : null}
          <div className="flex flex-col min-w-0 flex-1">
            <span className="truncate">{o.label}</span>
            {desc && (
              <span
                className={[
                  "text-[11px] text-muted-foreground/80 leading-snug mt-0.5",
                  wrapDescription ? "whitespace-normal break-words" : "truncate",
                ].join(" ")}
                data-testid={`text-option-description-${o.value}`}
              >
                {desc}
              </span>
            )}
          </div>
        </div>
      </SelectItem>
    );
  };
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled} open={open} onOpenChange={setOpen}>
      <SelectTrigger
        className={[
          "group h-auto min-h-[72px] rounded-xl px-3 py-2.5 transition-all duration-150",
          "focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
          "disabled:opacity-60 disabled:cursor-not-allowed",
          filled
            ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
            : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
        ].join(" ")}
        data-testid={testId}
      >
        <div className="flex items-center gap-3 w-full min-w-0">
          <div
            className={[
              "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
              filled
                ? "bg-primary/15 text-primary ring-primary/20"
                : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
            ].join(" ")}
          >
            {icon}
          </div>
          <div className="flex flex-col items-start min-w-0 flex-1 text-left">
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
              {label}
              {!filled && <span className="text-muted-foreground/40">*</span>}
            </span>
            {filled && selected ? (
              <div className="flex flex-col w-full min-w-0">
                <span className="text-sm truncate w-full leading-tight text-foreground font-semibold">
                  {selected.label}
                </span>
                {selectedDescription && (
                  <span
                    className="text-[11px] text-muted-foreground/80 truncate w-full leading-tight mt-0.5"
                    title={wrapDescription ? selectedDescription : undefined}
                  >
                    {selectedDescription}
                  </span>
                )}
              </div>
            ) : (
              <span className="text-sm truncate w-full leading-tight text-muted-foreground/60 font-normal">
                <SelectValue placeholder={placeholder} />
              </span>
            )}
          </div>
        </div>
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]">
        {showSearch && (
          <PickerSearchHeader
            inputRef={searchInputRef}
            query={query}
            onQueryChange={setQuery}
            testId={`input-search-${testId}`}
          />
        )}
        {showSearch && !hasAnyResult && <PickerNoMatches />}
        {groups
          ? filteredGroups.map((g) => (
              <SelectGroup key={g.label}>
                <SelectLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">
                  {g.label}
                </SelectLabel>
                {g.options.map(renderOption)}
              </SelectGroup>
            ))
          : filteredOptions.map(renderOption)}
      </SelectContent>
    </Select>
  );
}

export default function UgcExpressPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();

  const [durationSeconds, setDurationSeconds] = useState<15 | 30>(15);
  const [promotedItemType, setPromotedItemType] = useState<"product" | "offer">("product");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  // Selected product variant (null = "No specific variant"). Reset whenever
  // the product changes; product mode only. Sent with run creation so the
  // server snapshots the full variant on the run.
  const [selectedVariantName, setSelectedVariantName] = useState<string | null>(null);
  const [selectedOfferId, setSelectedOfferId] = useState<string>("");
  const [selectedPersonaIds, setSelectedPersonaIds] = useState<string[]>([]);
  const [personaOpen, setPersonaOpen] = useState(false);
  const [selectedStyleIds, setSelectedStyleIds] = useState<string[]>([]);
  const [styleOpen, setStyleOpen] = useState(false);

  const isOfferMode = promotedItemType === "offer";

  const { data: products = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: offers = [], isLoading: offersLoading } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });
  const { data: personas = [], isLoading: personasLoading } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });
  const { data: styles = [], isLoading: stylesLoading } = useQuery<UgcExpressStyleRecord[]>({
    queryKey: ["/api/ugc-express-styles"],
  });

  // Default the Style picker to the seeded Hyperrealistic UGC style once
  // styles load (and heal the selection if a chosen style gets archived).
  useEffect(() => {
    if (styles.length === 0) return;
    const valid = selectedStyleIds.filter((id) => styles.some((s) => s.id === id));
    if (valid.length > 0) {
      if (valid.length !== selectedStyleIds.length) setSelectedStyleIds(valid);
      return;
    }
    const def = styles.find((s) => s.value === UGC_EXPRESS_DEFAULT_STYLE_VALUE) ?? styles[0];
    setSelectedStyleIds([def.id]);
  }, [styles, selectedStyleIds]);

  // The main feed shows the CURRENT generation batch: one run per selected
  // style (each style is generated as its own separate run). Hydrated from the
  // server on mount so ALL in-progress runs survive refresh pinned up top
  // (including sibling runs parked at script approval — not just the newest).
  const [focusedRuns, setFocusedRuns] = useState<UgcExpressRun[]>([]);
  const { data: activeData, isLoading: activeLoading } = useQuery<{ runs: RunWithVideos[] }>({
    queryKey: [`/api/brands/${brandId}/ugc-express/runs/active`],
    enabled: !!brandId,
  });
  // Re-hydrate on brand switch: clear the focused runs so the new brand's
  // active runs take over instead of showing stale metadata.
  useEffect(() => {
    setFocusedRuns([]);
  }, [brandId]);
  useEffect(() => {
    if (focusedRuns.length === 0 && activeData?.runs?.length) {
      setFocusedRuns(activeData.runs.map((r) => r.run));
      // Seed each run's videos query from the active payload so the panel
      // renders instantly without a redundant initial fetch.
      for (const r of activeData.runs) {
        queryClient.setQueryData(
          [`/api/brands/${brandId}/ugc-express/runs/${r.run.id}/videos`],
          r.videos,
        );
      }
    }
  }, [activeData, focusedRuns, brandId]);

  const createRun = useMutation({
    // One run per selected style, created sequentially so each POST snapshots
    // its own style. Partial failures keep the runs that did start.
    mutationFn: async () => {
      const styleIds = selectedStyleIds.length > 0 ? selectedStyleIds : [undefined];
      const results: RunWithVideos[] = [];
      let firstError: any = null;
      for (const styleId of styleIds) {
        try {
          const res = await apiRequest("POST", `/api/brands/${brandId}/ugc-express/runs`, {
            durationSeconds,
            promotedItemType,
            productId: isOfferMode ? undefined : selectedProductId,
            offerId: isOfferMode ? selectedOfferId : undefined,
            personaIds: selectedPersonaIds,
            selectedVariantName: isOfferMode ? null : selectedVariantName,
            styleId,
          });
          results.push((await res.json()) as RunWithVideos);
        } catch (e) {
          if (!firstError) firstError = e;
        }
      }
      if (results.length === 0) throw firstError ?? new Error("Failed to create run");
      return { results, firstError };
    },
    onSuccess: ({ results, firstError }) => {
      // The new batch goes up top, but prior focused runs that still need
      // attention (a video pending / awaiting script approval / rendering)
      // stay pinned alongside it instead of dropping into Past Runs mid-flight.
      setFocusedRuns((prev) => {
        const newIds = new Set(results.map((r) => r.run.id));
        const stillInProgress = prev.filter((run) => {
          if (newIds.has(run.id)) return false;
          const vids =
            queryClient.getQueryData<UgcExpressVideo[]>([
              `/api/brands/${brandId}/ugc-express/runs/${run.id}/videos`,
            ]) ?? [];
          return vids.some((v) => IN_PROGRESS_STATUSES.has(v.status));
        });
        return [...results.map((r) => r.run), ...stillInProgress];
      });
      for (const r of results) {
        queryClient.setQueryData([`/api/brands/${brandId}/ugc-express/runs/${r.run.id}/videos`], r.videos);
      }
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ugc-express/runs`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ugc-express/runs/active`] });
      setSelectedPersonaIds([]);
      if (firstError) {
        toast({
          title: "Some styles could not start",
          description: `${results.length} of ${selectedStyleIds.length} style runs started. ${firstError?.message || ""}`.trim(),
          variant: "destructive",
        });
      } else {
        toast({
          title: "Generating UGC videos",
          description:
            results.length > 1
              ? `One run per style — ${results.length} styles, one video per persona each.`
              : "One review video per persona is being created.",
        });
      }
    },
    onError: (e: any) => {
      toast({ title: "Could not start", description: e?.message || "Failed to create run", variant: "destructive" });
    },
  });

  const togglePersona = (id: string) => {
    setSelectedPersonaIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleStyle = (id: string) => {
    setSelectedStyleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const hasPromotedItem = isOfferMode ? !!selectedOfferId : !!selectedProductId;
  const canGenerate =
    hasPromotedItem && selectedPersonaIds.length > 0 && selectedStyleIds.length > 0 && !createRun.isPending;
  const totalVideos = selectedPersonaIds.length * Math.max(selectedStyleIds.length, 1);

  return (
    <div className="mx-auto max-w-5xl space-y-5 px-8 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Video className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">UGC Express</h1>
          <p className="text-sm text-muted-foreground">
            Add personas, pick a product or offer, and generate a vertical UGC review video for each.
          </p>
        </div>
      </div>

      {/* Builder */}
      <Card>
        <CardContent className="space-y-5 p-6">
          {/* Header row with Product / Offer toggle — matches Ads Express */}
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 text-primary flex items-center justify-center shrink-0 ring-1 ring-primary/20 shadow-sm">
                <Wand2 className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-base font-semibold tracking-tight">Set your inputs</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Add personas, then pick a product or offer
                </p>
              </div>
            </div>
            <div
              className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60 backdrop-blur shadow-sm"
              data-testid="toggle-promoted-item"
            >
              <Button
                type="button"
                size="sm"
                variant={isOfferMode ? "ghost" : "default"}
                className="h-7 px-3 text-[11px] gap-1 rounded-full"
                onClick={() => {
                  if (createRun.isPending) return;
                  setPromotedItemType("product");
                  setSelectedOfferId("");
                }}
                disabled={createRun.isPending}
                data-testid="toggle-promoted-product"
              >
                <Package className="h-3 w-3" /> Product
              </Button>
              <Button
                type="button"
                size="sm"
                variant={isOfferMode ? "default" : "ghost"}
                className="h-7 px-3 text-[11px] gap-1 rounded-full"
                onClick={() => {
                  if (createRun.isPending) return;
                  setPromotedItemType("offer");
                  setSelectedProductId("");
                }}
                disabled={createRun.isPending}
                data-testid="toggle-promoted-offer"
              >
                <Sparkles className="h-3 w-3" /> Offer
              </Button>
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {/* Persona multi-picker */}
            <div className="space-y-2">
              <Popover open={personaOpen} onOpenChange={setPersonaOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    disabled={personasLoading}
                    data-testid="button-select-personas"
                    className={[
                      "group h-auto min-h-[72px] w-full rounded-xl px-3 py-2.5 border text-left transition-all duration-150",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
                      "disabled:opacity-60 disabled:cursor-not-allowed",
                      selectedPersonaIds.length > 0
                        ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                        : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
                    ].join(" ")}
                  >
                    <div className="flex items-center gap-3 w-full min-w-0">
                      <div
                        className={[
                          "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
                          selectedPersonaIds.length > 0
                            ? "bg-primary/15 text-primary ring-primary/20"
                            : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
                        ].join(" ")}
                      >
                        <Users className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                        <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
                          Personas
                          {selectedPersonaIds.length === 0 && <span className="text-muted-foreground/40">*</span>}
                        </span>
                        <span
                          className={[
                            "text-sm truncate w-full leading-tight",
                            selectedPersonaIds.length > 0 ? "text-foreground font-semibold" : "text-muted-foreground/60 font-normal",
                          ].join(" ")}
                        >
                          {selectedPersonaIds.length > 0
                            ? `${selectedPersonaIds.length} persona${selectedPersonaIds.length > 1 ? "s" : ""} selected`
                            : personasLoading ? "Loading…" : "Add personas"}
                        </span>
                      </div>
                      <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search personas…" />
                    <CommandList>
                      <CommandEmpty>No personas found.</CommandEmpty>
                      {personas.map((p, i) => {
                        const checked = selectedPersonaIds.includes(p.id);
                        return (
                          <CommandItem
                            key={p.id}
                            value={`${p.name} ${i}`}
                            onSelect={() => togglePersona(p.id)}
                            data-testid={`option-persona-${p.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${checked ? "opacity-100" : "opacity-0"}`} />
                            {p.imageUrl ? (
                              <img
                                src={thumbUrl(p.imageUrl, 64)!}
                                onError={thumbFallback(p.imageUrl)}
                                alt=""
                                className="mr-2 h-6 w-6 shrink-0 rounded-full object-cover"
                              />
                            ) : (
                              <UserCircle className="mr-2 h-6 w-6 shrink-0 text-muted-foreground" />
                            )}
                            <span className="truncate">{p.name}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Product / Offer picker — matches Ads Express */}
            {isOfferMode ? (
              <PickerSelect
                icon={<Sparkles className="h-4 w-4" />}
                label="Offer"
                placeholder={offers.length === 0 ? "No offers yet — add one in Offers" : "Choose offer"}
                value={selectedOfferId}
                onChange={(v) => {
                  setSelectedOfferId(v);
                  setSelectedProductId("");
                }}
                options={offers.map((o) => ({
                  value: o.id,
                  label: o.name,
                  description: (o as any).shortDescription || (o as any).summary || null,
                }))}
                testId="select-offer"
                searchable
                disabled={offersLoading || offers.length === 0 || createRun.isPending}
              />
            ) : (
              <PickerSelect
                icon={<Package className="h-4 w-4" />}
                label="Product"
                placeholder="Choose product"
                value={selectedProductId}
                onChange={(v) => {
                  setSelectedProductId(v);
                  setSelectedVariantName(null);
                  setSelectedOfferId("");
                }}
                options={[...products]
                  .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                  .map((p) => {
                    const media = (p as { mediaJson?: string[] | null }).mediaJson;
                    const first = Array.isArray(media) && media.length > 0 ? media[0] : null;
                    return { value: p.id, label: p.name, imageUrl: first };
                  })}
                testId="select-product"
                searchable
                disabled={productsLoading || createRun.isPending}
              />
            )}
          </div>

          {/* Variant picker — product mode only; renders nothing when the
              selected product has no variants (offers unaffected). */}
          {!isOfferMode && selectedProductId && (
            <VariantPicker
              product={products.find((p) => p.id === selectedProductId)}
              value={selectedVariantName}
              onChange={setSelectedVariantName}
              testIdPrefix="ugc-express-variant"
            />
          )}

          {/* Style multi-picker: each selected style becomes its own run */}
          <div className="space-y-2">
            <Popover open={styleOpen} onOpenChange={setStyleOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  disabled={stylesLoading || createRun.isPending}
                  data-testid="button-select-styles"
                  className={[
                    "group h-auto min-h-[72px] w-full rounded-xl px-3 py-2.5 border text-left transition-all duration-150",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                    selectedStyleIds.length > 0
                      ? "border-primary/30 bg-gradient-to-b from-primary/[0.06] to-primary/[0.02] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                      : "border-border/60 bg-background/60 hover:border-primary/30 hover:bg-background hover:shadow-sm",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-3 w-full min-w-0">
                    <div
                      className={[
                        "h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ring-1",
                        selectedStyleIds.length > 0
                          ? "bg-primary/15 text-primary ring-primary/20"
                          : "bg-muted/70 text-muted-foreground ring-border/60 group-hover:text-foreground",
                      ].join(" ")}
                    >
                      <Palette className="h-4 w-4" />
                    </div>
                    <div className="flex flex-col items-start min-w-0 flex-1 text-left">
                      <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-muted-foreground/70 leading-none mb-1.5 flex items-center gap-1">
                        Styles
                        {selectedStyleIds.length === 0 && <span className="text-muted-foreground/40">*</span>}
                      </span>
                      <span
                        className={[
                          "text-sm truncate w-full leading-tight",
                          selectedStyleIds.length > 0 ? "text-foreground font-semibold" : "text-muted-foreground/60 font-normal",
                        ].join(" ")}
                      >
                        {stylesLoading
                          ? "Loading…"
                          : selectedStyleIds.length > 0
                            ? selectedStyleIds
                                .map((id) => styles.find((s) => s.id === id)?.name)
                                .filter(Boolean)
                                .join(", ")
                            : "Choose styles"}
                      </span>
                    </div>
                    <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search styles…" />
                  <CommandList>
                    <CommandEmpty>No styles found.</CommandEmpty>
                    {styles.map((s, i) => {
                      const checked = selectedStyleIds.includes(s.id);
                      return (
                        <CommandItem
                          key={s.id}
                          value={`${s.name} ${i}`}
                          onSelect={() => toggleStyle(s.id)}
                          data-testid={`option-style-${s.id}`}
                        >
                          <Check className={`mr-2 h-4 w-4 shrink-0 ${checked ? "opacity-100" : "opacity-0"}`} />
                          {s.imageUrl ? (
                            <img src={thumbUrl(s.imageUrl, 64)!} onError={thumbFallback(s.imageUrl)} alt="" className="mr-2 h-8 w-8 shrink-0 rounded-md object-cover" />
                          ) : null}
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium">{s.name}</div>
                            {(s.shortDescription || s.description) && (
                              <div className="truncate text-xs text-muted-foreground">
                                {s.shortDescription || s.description}
                              </div>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {selectedStyleIds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedStyleIds.map((id) => {
                  const s = styles.find((x) => x.id === id);
                  if (!s) return null;
                  return (
                    <Badge key={id} variant="secondary" className="gap-1 pl-2 pr-1" data-testid={`chip-style-${id}`}>
                      {s.name}
                      <button
                        type="button"
                        onClick={() => toggleStyle(id)}
                        className="ml-1 rounded-sm p-0.5 hover:bg-background/60"
                        data-testid={`button-remove-style-${id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-background/60 px-3 py-2.5">
            <div>
              <p className="text-sm font-semibold">Video length</p>
              <p className="text-xs text-muted-foreground">Choose the duration for every video in this run.</p>
            </div>
            <div
              className="flex shrink-0 items-center gap-1 rounded-full border border-border/70 bg-muted/40 p-0.5"
              data-testid="selector-video-duration"
            >
              {([15, 30] as const).map((seconds) => (
                <Button
                  key={seconds}
                  type="button"
                  size="sm"
                  variant={durationSeconds === seconds ? "default" : "ghost"}
                  className="h-7 rounded-full px-3 text-[11px]"
                  onClick={() => setDurationSeconds(seconds)}
                  disabled={createRun.isPending}
                  data-testid={`button-duration-${seconds}`}
                >
                  {seconds} seconds
                </Button>
              ))}
            </div>
          </div>

          {/* Selected persona chips */}
          {selectedPersonaIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedPersonaIds.map((id) => {
                const p = personas.find((x) => x.id === id);
                if (!p) return null;
                return (
                  <Badge key={id} variant="secondary" className="gap-1 pl-1 pr-1" data-testid={`chip-persona-${id}`}>
                    {p.imageUrl ? (
                      <img src={thumbUrl(p.imageUrl, 64)!} onError={thumbFallback(p.imageUrl)} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" />
                    ) : (
                      <UserCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    {p.name}
                    <button
                      type="button"
                      onClick={() => togglePersona(id)}
                      className="ml-1 rounded-sm p-0.5 hover:bg-background/60"
                      data-testid={`button-remove-persona-${id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
          )}

          {/* Pronunciation Bank */}
          <PronunciationBank brandId={brandId!} />

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {selectedPersonaIds.length > 0
                ? selectedStyleIds.length > 1
                  ? `${totalVideos} videos will be generated — ${selectedStyleIds.length} styles × ${selectedPersonaIds.length} persona${selectedPersonaIds.length > 1 ? "s" : ""}, one run per style.`
                  : `${totalVideos} video${totalVideos > 1 ? "s" : ""} will be generated.`
                : `Each persona produces one ${durationSeconds}s 9:16 audio-on review video.`}
            </p>
            <Button onClick={() => createRun.mutate()} disabled={!canGenerate} data-testid="button-generate">
              {createRun.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Starting…</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" /> Generate videos</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current run — all videos from the current batch in ONE flat grid
          (Ads Express pattern). A new generation replaces the previous one;
          there is no history list. */}
      <div className="space-y-6">
        <h2 className="text-lg font-semibold">Current run</h2>
        {activeLoading && focusedRuns.length === 0 ? (
          <Skeleton className="h-40 w-full" />
        ) : focusedRuns.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
              <Video className="h-10 w-10 opacity-40" />
              <p className="text-sm">No runs yet. Pick a product and personas above to generate your first UGC videos.</p>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="panel-current-run">
            <CardContent className="p-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {focusedRuns.map((run) => (
                  <RunVideos key={run.id} brandId={brandId!} run={run} personas={personas} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Past runs — persistent brand-wide history below the current run
          (Task #1633, Ads Express "Recent generations" pattern). */}
      <UgcPastRuns brandId={brandId!} excludeIds={focusedRuns.map((r) => r.id)} personas={personas} />
    </div>
  );
}

// ── Past runs history (Task #1633) ──────────────────────────────────────────

function ugcRunStatusMeta(status: string): { label: string; variant: "default" | "secondary" | "destructive" | "outline" } {
  switch (status) {
    case "completed": return { label: "Completed", variant: "default" };
    case "failed": return { label: "Failed", variant: "destructive" };
    case "partial": return { label: "Partial", variant: "outline" };
    case "pending":
    case "running": return { label: "Running", variant: "secondary" };
    default: return { label: status, variant: "outline" };
  }
}

function UgcPastRuns({
  brandId,
  excludeIds,
  personas,
}: {
  brandId: string;
  excludeIds: string[];
  personas: Persona[];
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data: allRuns = [], isLoading } = useQuery<UgcExpressRun[]>({
    queryKey: [`/api/brands/${brandId}/ugc-express/runs`],
    enabled: !!brandId,
    staleTime: 30_000,
  });

  const excluded = new Set(excludeIds);
  const pastRuns = useMemo(
    () =>
      allRuns
        .filter((r) => !excluded.has(r.id))
        .sort((a, b) => new Date(b.createdAt as any).getTime() - new Date(a.createdAt as any).getTime()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRuns, excludeIds.join(",")],
  );

  return (
    <PastRunsSection title="Past runs" count={pastRuns.length} isLoading={isLoading} testIdPrefix="ugc-past-runs">
      {pastRuns.map((run) => {
        // A past-list run with a video still parked at the script-approval
        // gate (legacy data / another user's run) gets an explicit "Review
        // script" label instead of falling through to a raw status string.
        const meta = (run as any).needsScriptReview
          ? ({ label: "Review script", variant: "outline" } as const)
          : ugcRunStatusMeta(run.status);
        const isOpen = expandedId === run.id;
        return (
          <div key={run.id} className="rounded-lg border border-border/50 bg-card" data-testid={`card-past-run-${run.id}`}>
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : run.id)}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
              data-testid={`button-toggle-past-run-${run.id}`}
            >
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`text-past-run-date-${run.id}`}>
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                  {formatRunWhen(run.createdAt as any)}
                </span>
                <Badge variant={meta.variant} className="text-[10px]" data-testid={`status-past-run-${run.id}`}>
                  {meta.label}
                </Badge>
                {(run as any).styleName && (
                  <Badge variant="outline" className="text-[10px]">{(run as any).styleName}</Badge>
                )}
                <Badge variant="outline" className="text-[10px]" data-testid={`duration-past-run-${run.id}`}>
                  {(run as any).durationSeconds ?? 15}s
                </Badge>
              </div>
              {isOpen ? (
                <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
            </button>
            {isOpen && (
              <div className="px-3 pb-3">
                <UgcPastRunVideos brandId={brandId} run={run} personas={personas} />
              </div>
            )}
          </div>
        );
      })}
    </PastRunsSection>
  );
}

// Lazily fetches ONE past run's videos when its row expands and renders them
// with the same VideoCard tiles the current run uses (view + download).
function UgcPastRunVideos({
  brandId,
  run,
  personas,
}: {
  brandId: string;
  run: UgcExpressRun;
  personas: Persona[];
}) {
  const { data: videos = [], isLoading, isError } = useQuery<UgcExpressVideo[]>({
    queryKey: [`/api/brands/${brandId}/ugc-express/runs/${run.id}/videos`],
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 pt-2 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="aspect-[9/16] rounded-lg" />
        <Skeleton className="aspect-[9/16] rounded-lg" />
      </div>
    );
  }
  if (isError) {
    return <p className="text-xs text-destructive pt-2" data-testid={`text-past-run-error-${run.id}`}>Couldn't load this run's videos.</p>;
  }
  if (videos.length === 0) {
    return <p className="text-xs text-muted-foreground pt-2" data-testid={`text-past-run-empty-${run.id}`}>No videos in this run.</p>;
  }
  return (
    <div className="grid gap-4 pt-2 sm:grid-cols-2 lg:grid-cols-3">
      {videos.map((video) => (
        <VideoCard
          key={video.id}
          brandId={brandId}
          video={video}
          persona={personas.find((p) => p.id === video.personaId)}
          styleName={(run as any).styleName || undefined}
          durationSeconds={(run as any).durationSeconds ?? 15}
        />
      ))}
    </div>
  );
}

// Renders one run's videos as bare grid cells so multiple style runs flatten
// into the SAME grid under "Current run" (Ads Express pattern). Each run keeps
// its own videos query + polling.
function RunVideos({
  brandId,
  run,
  personas,
}: {
  brandId: string;
  run: UgcExpressRun;
  personas: Persona[];
}) {
  const { data: videos = [] } = useQuery<UgcExpressVideo[]>({
    queryKey: [`/api/brands/${brandId}/ugc-express/runs/${run.id}/videos`],
    refetchInterval: (query) => {
      const rows = (query.state.data as UgcExpressVideo[] | undefined) ?? [];
      return rows.some((v) => ACTIVE_STATUSES.has(v.status)) ? 4000 : false;
    },
  });

  return (
    <>
      {videos.map((video) => (
        <VideoCard
          key={video.id}
          brandId={brandId}
          video={video}
          persona={personas.find((p) => p.id === video.personaId)}
          styleName={(run as any).styleName || undefined}
          durationSeconds={(run as any).durationSeconds ?? 15}
        />
      ))}
    </>
  );
}

function VideoCard({
  brandId,
  video,
  persona,
  styleName,
  durationSeconds,
}: {
  brandId: string;
  video: UgcExpressVideo;
  persona?: Persona;
  styleName?: string;
  durationSeconds: number;
}) {
  const { toast } = useToast();
  const meta = statusMeta(video.status);
  const StatusIcon = meta.icon;
  const spinning = video.status === "pending" || video.status === "rendering";

  // Local edit buffer for the spoken script while the video is parked at the
  // approval gate. Re-seeded whenever the server value changes (e.g. a retry
  // re-parked the video with a fresh script).
  const [scriptDraft, setScriptDraft] = useState(video.spokenScript ?? "");
  useEffect(() => {
    setScriptDraft(video.spokenScript ?? "");
  }, [video.spokenScript]);

  const approve = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/ugc-express/videos/${video.id}/approve`, { script: scriptDraft });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ugc-express/runs/${video.runId}/videos`] });
      toast({ title: "Script approved", description: "Rendering this video now." });
    },
    onError: (e: any) => {
      toast({ title: "Approval failed", description: e?.message || "Could not approve script", variant: "destructive" });
    },
  });

  const retry = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/brands/${brandId}/ugc-express/videos/${video.id}/retry`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ugc-express/runs/${video.runId}/videos`] });
      toast({ title: "Retrying", description: "Regenerating this video." });
    },
    onError: (e: any) => {
      toast({ title: "Retry failed", description: e?.message || "Could not retry", variant: "destructive" });
    },
  });

  return (
    <div className="overflow-hidden rounded-lg border bg-card" data-testid={`card-video-${video.id}`}>
      <div className="relative aspect-[9/16] w-full bg-muted">
        {video.status === "ready" && video.videoUrl ? (
          <video
            src={video.videoUrl}
            controls
            playsInline
            className="h-full w-full object-cover"
            data-testid={`player-video-${video.id}`}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-4 text-center">
            {video.status === "failed" ? (
              <>
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <p className="text-xs text-muted-foreground line-clamp-3">{video.error || "Generation failed."}</p>
                <Button size="sm" variant="outline" onClick={() => retry.mutate()} disabled={retry.isPending} data-testid={`button-retry-${video.id}`}>
                  {retry.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Retry
                </Button>
              </>
            ) : video.status === "awaiting_approval" ? (
              <>
                <Pencil className="h-8 w-8 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Review the spoken script below, then approve to render.</p>
              </>
            ) : (
              <>
                <StatusIcon className={`h-8 w-8 text-muted-foreground ${spinning ? "animate-spin" : ""}`} />
                <p className="text-xs text-muted-foreground">{meta.label}…</p>
              </>
            )}
          </div>
        )}
      </div>
      {video.status === "awaiting_approval" && (
        <div className="space-y-2 border-t p-3">
          <p className="text-xs font-medium text-muted-foreground">Spoken script</p>
          <Textarea
            value={scriptDraft}
            onChange={(e) => setScriptDraft(e.target.value)}
            rows={5}
            className="text-sm"
            placeholder="What the person says out loud…"
            disabled={approve.isPending}
            data-testid={`input-script-${video.id}`}
          />
          <Button
            size="sm"
            className="w-full"
            onClick={() => approve.mutate()}
            disabled={approve.isPending || !scriptDraft.trim()}
            data-testid={`button-approve-${video.id}`}
          >
            {approve.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
            Approve & render
          </Button>
        </div>
      )}
      <div className="flex items-center justify-between gap-2 p-3">
        <div className="flex min-w-0 items-center gap-2">
          {persona?.imageUrl ? (
            <img src={thumbUrl(persona.imageUrl, 64)!} onError={thumbFallback(persona.imageUrl)} alt={persona.name} className="h-7 w-7 shrink-0 rounded-full object-cover" />
          ) : (
            <UserCircle className="h-7 w-7 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <span className="block truncate text-sm font-medium" data-testid={`text-persona-${video.id}`}>
              {persona?.name || "Persona"}
            </span>
            {styleName && (
              <span className="block truncate text-xs text-muted-foreground" data-testid={`text-style-${video.id}`}>
                {styleName}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Badge variant="outline" className="text-[10px]" data-testid={`duration-video-${video.id}`}>
            {durationSeconds}s
          </Badge>
          <Badge variant={meta.variant} className="gap-1">
            <StatusIcon className={`h-3 w-3 ${spinning ? "animate-spin" : ""}`} />
            {meta.label}
          </Badge>
          {video.status === "ready" && video.videoUrl && (
            <a href={video.videoUrl} download target="_blank" rel="noreferrer" data-testid={`link-download-${video.id}`}>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <Download className="h-4 w-4" />
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// Per-brand Pronunciation Bank: a persistent, structured word → spoken-form
// dictionary that is auto-applied to the spoken dialogue of EVERY UGC Express
// generation (never to on-screen text or the product's real spelling). Strictly
// per-brand and empty by default. Every entry auto-saves the moment it is added,
// edits persist on blur, and deletes apply immediately — there is no bulk "Save
// bank" step and no per-run override. A fully-typed new row is ALSO committed
// when the user leaves it (blur) or closes the dialog (Done / overlay /
// Escape), so typed entries are never silently discarded.
// Exported for the DOM regression suite (pronunciationBank.test.tsx).
export function PronunciationBank({ brandId }: { brandId: string }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [newWord, setNewWord] = useState("");
  const [newSpoken, setNewSpoken] = useState("");
  // Local edit buffer for the existing rows, keyed by entry id. Seeded from the
  // server data and re-synced whenever it changes, so the server stays the
  // source of truth while a field is being typed.
  const [edits, setEdits] = useState<Record<string, { word: string; spoken: string }>>({});

  const listKey = `/api/brands/${brandId}/ugc-express/pronunciations`;
  const { data, isLoading } = useQuery<UgcExpressPronunciation[]>({
    queryKey: [listKey],
    enabled: !!brandId,
  });
  const bank = useMemo(() => data ?? [], [data]);

  useEffect(() => {
    const rows = data ?? [];
    setEdits(Object.fromEntries(rows.map((r) => [r.id, { word: r.word, spoken: r.spoken }])));
  }, [data]);

  const createMut = useMutation({
    mutationFn: async (body: { word: string; spoken: string }) => {
      const res = await apiRequest("POST", listKey, body);
      return (await res.json()) as UgcExpressPronunciation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [listKey] });
      setNewWord("");
      setNewSpoken("");
    },
    onError: (e: any) => {
      toast({ title: "Could not add", description: e?.message || "Failed to add pronunciation", variant: "destructive" });
    },
  });

  const updateMut = useMutation({
    mutationFn: async (body: { id: string; word: string; spoken: string }) => {
      const res = await apiRequest("PATCH", `${listKey}/${body.id}`, { word: body.word, spoken: body.spoken });
      return (await res.json()) as UgcExpressPronunciation;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [listKey] }),
    onError: (e: any) => {
      toast({ title: "Could not save change", description: e?.message || "Failed to update pronunciation", variant: "destructive" });
      // Revert the local buffer to the server truth.
      queryClient.invalidateQueries({ queryKey: [listKey] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `${listKey}/${id}`);
      return id;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [listKey] }),
    onError: (e: any) => {
      toast({ title: "Could not delete", description: e?.message || "Failed to delete pronunciation", variant: "destructive" });
      queryClient.invalidateQueries({ queryKey: [listKey] });
    },
  });

  // Guard against double-submits: blur of a new-row field and a click on the
  // + / Done button can both try to commit the same pending entry in quick
  // succession, before react-query's isPending re-render lands. A synchronous
  // ref closes that window. On failure the typed values stay in the inputs
  // (they are only cleared onSuccess) so the user can retry.
  const addInFlightRef = useRef(false);
  const commitNew = () => {
    const word = newWord.trim();
    const spoken = newSpoken.trim();
    if (!word || !spoken || addInFlightRef.current) return;
    addInFlightRef.current = true;
    createMut.mutate(
      { word, spoken },
      { onSettled: () => { addInFlightRef.current = false; } },
    );
  };

  // Closing the dialog (Done button, overlay click, Escape) must never
  // silently discard a typed-but-uncommitted new entry. Both fields filled →
  // save it as part of closing; only one filled → keep the dialog open and
  // warn instead of dropping the text.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Escape/overlay close doesn't blur the focused input, so commit any
      // dirty existing-row edits here too (commitEdit skips clean/invalid/
      // already-in-flight rows).
      for (const row of bank) commitEdit(row);
      const word = newWord.trim();
      const spoken = newSpoken.trim();
      if (word && spoken) {
        commitNew();
      } else if (word || spoken) {
        toast({
          title: "Unsaved entry",
          description: !word
            ? "Add the word on the left to save this entry, or clear both fields to close without saving."
            : "Add how to say it on the right to save this entry, or clear both fields to close without saving.",
          variant: "destructive",
        });
        return; // keep the dialog open — don't silently drop the text
      }
    }
    setOpen(next);
  };

  // Per-row in-flight guard: blur and dialog-close can both try to commit the
  // same dirty row back-to-back (before the server row updates), which would
  // fire a duplicate PATCH. Synchronous, so the second caller sees it.
  const updateInFlightRef = useRef<Set<string>>(new Set());
  const commitEdit = (row: UgcExpressPronunciation) => {
    const e = edits[row.id];
    if (!e) return;
    const word = e.word.trim();
    const spoken = e.spoken.trim();
    // Both fields are required; ignore an empty field (leaves the saved value
    // intact) and skip when nothing actually changed.
    if (!word || !spoken) return;
    if (word === row.word && spoken === row.spoken) return;
    if (updateInFlightRef.current.has(row.id)) return;
    updateInFlightRef.current.add(row.id);
    updateMut.mutate(
      { id: row.id, word, spoken },
      { onSettled: () => { updateInFlightRef.current.delete(row.id); } },
    );
  };

  const setEdit = (id: string, patch: Partial<{ word: string; spoken: string }>) =>
    setEdits((prev) => ({ ...prev, [id]: { word: prev[id]?.word ?? "", spoken: prev[id]?.spoken ?? "", ...patch } }));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm font-medium">
          <Volume2 className="h-4 w-4 text-muted-foreground" /> Pronunciation Bank
        </label>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="button-manage-pronunciations">
              <Pencil className="mr-2 h-3.5 w-3.5" /> Manage
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Pronunciation Bank</DialogTitle>
              <DialogDescription>
                Applied to the spoken dialogue of every UGC Express video — never to on-screen text or the product's real spelling. Changes save automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[50vh] space-y-2 overflow-y-auto py-1">
              <div className="flex items-center gap-2 px-0.5 text-xs font-medium text-muted-foreground">
                <span className="flex-1">Word</span>
                <span className="w-5" />
                <span className="flex-1">How to say it</span>
                <span className="w-8" />
              </div>
              {bank.map((row) => (
                <div key={row.id} className="flex items-center gap-2" data-testid={`row-pronunciation-${row.id}`}>
                  <Input
                    value={edits[row.id]?.word ?? row.word}
                    onChange={(e) => setEdit(row.id, { word: e.target.value })}
                    onBlur={() => commitEdit(row)}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                    placeholder="tallow"
                    className="flex-1"
                    data-testid={`input-word-${row.id}`}
                  />
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <Input
                    value={edits[row.id]?.spoken ?? row.spoken}
                    onChange={(e) => setEdit(row.id, { spoken: e.target.value })}
                    onBlur={() => commitEdit(row)}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                    placeholder="tal-oh"
                    className="flex-1"
                    data-testid={`input-spoken-${row.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteMut.mutate(row.id)}
                    data-testid={`button-remove-row-${row.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {bank.length === 0 && !isLoading && (
                <p className="py-4 text-center text-sm text-muted-foreground">No entries yet. Add your first word below.</p>
              )}
              {/* Add row — saves on Add / Enter, on blur once both fields are
                  filled, and as part of closing the dialog (no bulk save). */}
              <div className="flex items-center gap-2 pt-1" data-testid="row-pronunciation-new">
                <Input
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commitNew(); }}
                  onBlur={commitNew}
                  placeholder="tallow"
                  className="flex-1"
                  data-testid="input-word-new"
                />
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Input
                  value={newSpoken}
                  onChange={(e) => setNewSpoken(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") commitNew(); }}
                  onBlur={commitNew}
                  placeholder="tal-oh"
                  className="flex-1"
                  data-testid="input-spoken-new"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={commitNew}
                  disabled={!newWord.trim() || !newSpoken.trim() || createMut.isPending}
                  data-testid="button-add-row"
                >
                  {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)} data-testid="button-close-pronunciations">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {isLoading ? (
        <Skeleton className="h-6 w-64" />
      ) : bank.length === 0 ? (
        <p className="text-xs text-muted-foreground">No custom pronunciations yet.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="list-pronunciation-pills">
          {bank.map((r) => (
            <Badge key={r.id} variant="secondary" className="gap-1 font-normal" data-testid={`pill-pronunciation-${r.id}`}>
              <span className="font-medium">{r.word}</span>
              <ArrowRight className="h-3 w-3 opacity-60" />
              {r.spoken}
            </Badge>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Saved per brand and auto-applied to spoken dialogue in every generation.
      </p>
    </div>
  );
}
